import { useForm } from '@mantine/form';
import { noop } from 'lodash';
import { FormProps } from 'components/forms/FormProps';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import useLoadingAction from 'hooks/use-loading-action';
import { SimpleGrid, TextInput, PasswordInput, Select } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import FormInputGroup from 'components/forms/FormInputGroup';
import FormWrapper from 'components/forms/FormWrapper';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { UserSelect } from 'components/selects/UserSelect';
import { useMemo } from 'react';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

/**
 * The data collected from the form.
 */
export interface RemoteSourceFormData {
  name: string;
  type: string;
  url?: string;
  login?: string;
  password?: string;
  incomingApiKey?: string;
  userId?: string;
}

/**
 * The default initial values.
 */
export const remoteSourceFormInitialValues: RemoteSourceFormData = {
  name: '',
  type: 'chastia',
  url: '',
  login: '',
  password: '',
  incomingApiKey: '',
  userId: '',
};

/**
 * Parameters of the RemoteSourceForm.
 */
export interface RemoteSourceFormProps extends FormProps<RemoteSourceFormData> {
  readOnly?: Partial<Record<keyof RemoteSourceFormData, boolean>>;
  primaryButtonText?: string;
  primaryIcon?: React.ReactNode;
}

/**
 * Form for creating and editing remote source.
 */
export default function RemoteSourceForm({
  initialValues = remoteSourceFormInitialValues,
  onSubmit = noop,
  readOnly = {},
  primaryButtonText = 'Uložiť',
  primaryIcon = <IconCheck stroke="1.5" height={24} width={24} />,
}: RemoteSourceFormProps) {
  const types = useMemo(
    () => [
      { value: 'chastia', label: 'Chastia' },
      { value: 'xlsx', label: 'XLSX' },
    ],
    []
  );

  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const { isTablet } = useResponsiveDesign();

  /**
   * Shared validator for fields that are optional when the XLSX type is selected.
   */
  const optionalWhenXlsx = (value?: string, values?: RemoteSourceFormData) =>
    values?.type === 'xlsx' ? null : required(value)?.message ?? null;

  const form = useForm<RemoteSourceFormData>({
    initialValues,
    validate: {
      name: createValidator([required]),
      type: createValidator([required]),
      url: optionalWhenXlsx,
      login: optionalWhenXlsx,
      password: optionalWhenXlsx,
      incomingApiKey: optionalWhenXlsx,
      userId: optionalWhenXlsx,
    },
  });

  useDirtyFormHandler(form);
  const isXlsxType = form.values.type === 'xlsx';

  return (
    <FormWrapper
      onSubmit={form.onSubmit(submit)}
      loading={loading}
      primaryButtonText={primaryButtonText}
      primaryIcon={primaryIcon}
      skipSecondaryButtonConfirm={!form.isDirty()}
    >
      <FormInputGroup groupTitle="Základné informácie">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <TextInput
            label="Názov"
            placeholder="Názov externého zdroja"
            size="lg"
            disabled={readOnly.name}
            {...form.getInputProps('name')}
          />
          <Select
            label="Typ"
            placeholder="Typ externého zdroja"
            size="lg"
            disabled={readOnly.type}
            data={types}
            {...form.getInputProps('type')}
          />
        </SimpleGrid>
      </FormInputGroup>

      {!isXlsxType && (
        <FormInputGroup groupTitle="Prístupové údaje">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <TextInput
              label="URL"
              placeholder="URL externého zdroja"
              size="lg"
              disabled={readOnly.url}
              {...form.getInputProps('url')}
            />
            <TextInput
              label="API kľúč (Objekto)"
              placeholder="Vstupný API kľúč"
              size="lg"
              disabled={readOnly.incomingApiKey}
              {...form.getInputProps('incomingApiKey')}
            />
            <TextInput
              label="Login"
              placeholder="Prihlasovacie meno"
              size="lg"
              disabled={readOnly.login}
              {...form.getInputProps('login')}
            />
            <PasswordInput
              label="Heslo"
              placeholder="Heslo"
              size="lg"
              disabled={readOnly.password}
              {...form.getInputProps('password')}
            />
          </SimpleGrid>
        </FormInputGroup>
      )}

      {!isXlsxType && (
        <FormInputGroup groupTitle="Priradenie">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <UserSelect
              label="Importujúci používateľ"
              placeholder="Vyberte importujúceho používateľa"
              size="lg"
              disabled={readOnly.userId}
              clearable
              {...form.getInputProps('userId')}
            />
          </SimpleGrid>
        </FormInputGroup>
      )}
    </FormWrapper>
  );
}
